// web/src/components/NeedsActionItem.tsx — left accent (warn for spec/clarify, run for merge) + CTA (D05).
// Portal spec 18 adds `suggestions`: a live site's monthly ideas wait for answers; the button jumps to that site's card.
import { useNavigate } from 'react-router-dom';
import type { NeedsAction, RunSummary } from '../lib/types';
import { Icon } from './Icon';

const COPY = {
  spec: { label: 'Approve spec', icon: 'file', accent: 'border-l-warn' },
  clarify: { label: 'Answer question', icon: 'help', accent: 'border-l-warn' },
  merge: { label: 'Review & merge', icon: 'git', accent: 'border-l-run' },
  // Chalice tasks (CHALICE §5 C2): a question in the chat, a preview waiting on "Make it live".
  question: { label: 'Answer question', icon: 'help', accent: 'border-l-warn' },
  preview: { label: 'Review preview', icon: 'image', accent: 'border-l-run' },
} as const;

export function NeedsActionItem({ item, siteName }: { item: NeedsAction; siteName?: string }) {
  if (item.kind === 'suggestions') return <SuggestionsItem siteId={item.siteId} count={item.count} siteName={siteName} />;
  return <RunItem kind={item.kind} run={item.run} />;
}

function RunItem({ kind, run }: { kind: keyof typeof COPY; run: RunSummary }) {
  const nav = useNavigate();
  const c = COPY[kind];
  return (
    <div className={`flex items-center justify-between gap-3 rounded-lg border border-hair border-l-4 ${c.accent} bg-surface p-3`}>
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-ink">{run.title}</div>
        {run.siteName && <div className="text-xs text-muted">{run.siteName}</div>}
      </div>
      <button onClick={() => nav(`/requests/${run.id}`)}
        className="inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-md border border-hair px-3 text-sm text-ink transition-transform active:scale-[0.98]">
        <Icon name={c.icon} size={15} />{c.label}
      </button>
    </div>
  );
}

function SuggestionsItem({ siteId, count, siteName }: { siteId: string; count: number; siteName?: string }) {
  const show = () => document.getElementById(`suggestions-${siteId}`)?.scrollIntoView({ block: 'start' });
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-hair border-l-4 border-l-run bg-surface p-3">
      <div className="min-w-0">
        <div className="text-sm font-medium text-ink">Your monthly ideas are ready</div>
        <div className="text-xs text-muted">{siteName ? `${siteName} · ` : ''}{count === 1 ? '1 idea needs your answer' : `${count} ideas need your answer`}</div>
      </div>
      <button onClick={show}
        className="inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-md border border-hair px-3 text-sm text-ink transition-transform active:scale-[0.98]">
        <Icon name="check" size={15} />See ideas
      </button>
    </div>
  );
}
